import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { LabourCategory, LabourCategoryCreateDto } from '../../models/labour-category.interface';
import { parametersFromPath } from '@app/core/interceptors/mock-api.interceptor';
import { ApiResponse } from '@app/core/adaptors/dto';

@Injectable({ providedIn: 'root' })
export class LabourCategoryMocksService {
  private readonly STORAGE_KEY = 'mock_labour_categories';
  private labourCategories: LabourCategory[] = [];

  constructor() {
    this.loadFromStorage();
  }

  handleMockRequest(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const { tenantId, resourceId } = parametersFromPath(req, 'labour-categories');

    if (req.method === 'GET') {
      if (resourceId) {
        return this.getById(resourceId);
      } else {
        const url = new URL(req.url);
        const query = url.searchParams.get('q') || '';
        return this.getAll(tenantId, query);
      }
    } else if (req.method === 'POST') {
      const dto = req.body as LabourCategoryCreateDto;
      return this.create(tenantId, dto);
    } else if (req.method === 'PUT' && resourceId) {
      const dto = req.body as Partial<LabourCategoryCreateDto>;
      return this.update(resourceId, dto);
    } else if (req.method === 'DELETE' && resourceId) {
      return this.delete(resourceId);
    } else {
      throw new Error(`Unsupported request: ${req.method} ${req.url}`);
    }
  }

  private getAll(tenantId: number, query: string): Observable<HttpEvent<any>> {
    if (this.labourCategories.length === 0) {
      this.generateMockData(tenantId);
      this.saveToStorage();
    }

    let filtered = this.labourCategories.filter((lc) => lc.tenantId === tenantId);

    if (query?.trim()) {
      const search = query.toLowerCase().trim();
      filtered = filtered.filter(
        (lc) =>
          lc.code.toLowerCase().includes(search) ||
          lc.name.toLowerCase().includes(search) ||
          (lc.description && lc.description.toLowerCase().includes(search))
      );
    }

    const res: ApiResponse<LabourCategory> = {
      data: filtered,
      total: filtered.length,
      page: 1,
      limit: filtered.length || 10,
      totalPages: 1,
    };

    return this.mapToResponse(res, 200);
  }

  private getById(id: number): Observable<HttpEvent<any>> {
    const category = this.labourCategories.find((lc) => lc.id === id);
    if (category) {
      return this.mapToResponse(category, 200);
    }
    return this.mapToResponse({ message: 'Labour category not found' }, 404);
  }

  private create(tenantId: number, dto: LabourCategoryCreateDto): Observable<HttpEvent<any>> {
    const newCategory: LabourCategory = {
      id: this.getNextId(),
      tenantId,
      code: dto.code,
      name: dto.name,
      description: dto.description ?? null,
      hourlyCost: dto.hourlyCost ?? null,
      hourlyCharge: dto.hourlyCharge ?? null,
      allowDiscount: dto.allowDiscount ?? false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this.labourCategories.push(newCategory);
    this.saveToStorage();
    return this.mapToResponse(newCategory, 201);
  }

  private update(id: number, dto: Partial<LabourCategoryCreateDto>): Observable<HttpEvent<any>> {
    const index = this.labourCategories.findIndex((lc) => lc.id === id);
    if (index !== -1) {
      this.labourCategories[index] = {
        ...this.labourCategories[index],
        ...dto,
        updatedAt: new Date().toISOString(),
      };
      this.saveToStorage();
      return this.mapToResponse(this.labourCategories[index], 200);
    }
    return this.mapToResponse({ message: 'Labour category not found' }, 404);
  }

  private delete(id: number): Observable<HttpEvent<any>> {
    this.labourCategories = this.labourCategories.filter((lc) => lc.id !== id);
    this.saveToStorage();
    return this.mapToResponse({}, 200);
  }

  getMockLabourCategories(): LabourCategory[] {
    if (this.labourCategories.length === 0) {
      this.generateMockData(1);
      this.saveToStorage();
    }
    return [...this.labourCategories];
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.labourCategories = JSON.parse(stored);
        console.log(`Loaded ${this.labourCategories.length} labour categories from localStorage`);
      }
    } catch (error) {
      console.error('Error loading labour categories from localStorage:', error);
      this.labourCategories = [];
    }
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.labourCategories));
      console.log(`Saved ${this.labourCategories.length} labour categories to localStorage`);
    } catch (error) {
      console.error('Error saving labour categories to localStorage:', error);
    }
  }

  private mapToResponse<T>(res: T, statusCode: number): Observable<HttpEvent<T>> {
    return of(
      new HttpResponse({
        status: statusCode,
        body: res,
      })
    ).pipe(delay(500));
  }

  private generateMockData(tenantId: number): void {
    const categories = [
      {
        code: 'MECH',
        name: 'Mechanical',
        description: 'General mechanical work',
        hourlyCost: 45,
        hourlyCharge: 95,
      },
      {
        code: 'ELEC',
        name: 'Electrical',
        description: 'Electrical diagnostics and repairs',
        hourlyCost: 50,
        hourlyCharge: 105,
      },
      {
        code: 'DIAG',
        name: 'Diagnostics',
        description: 'Vehicle diagnostics and scanning',
        hourlyCost: 55,
        hourlyCharge: 120,
      },
      {
        code: 'BODY',
        name: 'Body Work',
        description: 'Panel beating and body repairs',
        hourlyCost: 40,
        hourlyCharge: 85,
      },
      {
        code: 'PAINT',
        name: 'Paint',
        description: 'Spray painting and touch-ups',
        hourlyCost: 42,
        hourlyCharge: 90,
      },
      {
        code: 'AIRCON',
        name: 'Air Conditioning',
        description: 'A/C service and repairs',
        hourlyCost: 48,
        hourlyCharge: 100,
      },
      {
        code: 'TRANS',
        name: 'Transmission',
        description: 'Gearbox and transmission work',
        hourlyCost: 55,
        hourlyCharge: 115,
      },
      {
        code: 'GENERAL',
        name: 'General',
        description: 'General labour',
        hourlyCost: 35,
        hourlyCharge: 75,
      },
    ];

    categories.forEach((cat, index) => {
      this.labourCategories.push({
        id: index + 1,
        tenantId,
        code: cat.code,
        name: cat.name,
        description: cat.description,
        hourlyCost: cat.hourlyCost,
        hourlyCharge: cat.hourlyCharge,
        allowDiscount: index % 2 === 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    });
  }

  private getNextId(): number {
    return this.labourCategories.length > 0
      ? Math.max(...this.labourCategories.map((lc) => lc.id)) + 1
      : 1;
  }
}
